import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCheck, faExclamationTriangle, faInfoCircle, faTimes } from '@fortawesome/free-solid-svg-icons';
import { AnimatePresence } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useApi } from '../../hooks/useApi';
import ConfirmationToast from './ConfirmationToast';
import ProgressToast from './ProgressToast';
import BaseToast from './Toast';
import { type Toast as BaseToastType, ToastType, useToastStore } from './useToasts';

// Renders the fixed toast stack and keeps progress/download toasts in sync with the backend
const ToastManager = () => {
    const api = useApi();
    const { toasts, updateToasts } = useToastStore((state) => ({
        toasts: state.toasts,
        updateToasts: state.updateToasts
    }));
    const fetchIntervalActive = useRef(false);
    const toastsActive = useMemo(
        () => toasts.filter((t) => ['progress', 'download'].includes(t.type)).length > 0,
        [toasts]
    );
    const refreshInterval = 2000;

    // Polls the backend for the current state of all progress/download toasts
    const fetchUpdatedToasts = useCallback(() => {
        void api({
            url: 'toasts/progress',
            method: 'POST',
            data: {
                toasts: toasts
                    .filter((t) => ['progress', 'download'].includes(t.type))
                    .map((t) => ({
                        id: t.id,
                        typeId: t.typeId,
                        type: t.type
                    }))
            },
            onSuccess: updateToasts
        });
    }, [toasts, updateToasts]);

    useEffect(() => {
        if (!fetchIntervalActive.current && toastsActive) {
            fetchIntervalActive.current = true;
            const interval = setInterval(() => {
                if (!document.hidden) fetchUpdatedToasts();
            }, refreshInterval);

            return () => {
                fetchIntervalActive.current = false;
                clearInterval(interval);
            };
        }
    }, [toastsActive, fetchUpdatedToasts]);

    // Maps a toast type to the icon shown in the colored badge
    const resolveIconFromType = (type: ToastType): IconProp => {
        switch (type) {
            case 'alert':
                return faExclamationTriangle;
            case 'error':
                return faTimes;
            case 'success':
                return faCheck;
            default:
                return faInfoCircle;
        }
    };

    // Picks the toast component matching the toast type
    const renderToast = useCallback((toast: BaseToastType) => {
        switch (toast.type) {
            case 'progress':
            case 'download':
                return <ProgressToast key={toast.id} toast={toast} />;
            case 'confirmation':
                return <ConfirmationToast key={toast.id} toast={toast} />;
            default:
                return (
                    <BaseToast
                        removeAfter={5000}
                        key={toast.id}
                        title={toast.title}
                        description={toast.description}
                        icon={resolveIconFromType(toast.type)}
                        color={toast.type as any}
                        toastId={toast.id}
                    />
                );
        }
    }, []);

    return (
        <div className="tw-pointer-events-none tw-fixed tw-inset-x-3 tw-bottom-3 tw-z-[99999] tw-flex tw-flex-col tw-items-end tw-gap-2 sm:tw-inset-x-auto sm:tw-bottom-4 sm:tw-right-4">
            <AnimatePresence>{toasts.map(renderToast)}</AnimatePresence>
        </div>
    );
};

export default ToastManager;
